/**
 * UI permission-keys for the `CRM` route — the Agency CRM tool (`/crm/*`, ENT-565).
 *
 * Route name matches the BE CSV catalogue rows (acl-svc `config/ui-permissions.csv`, the `CRM`
 * block, seeded by migration `20261030120000-seed-crm-ui-map.ts`, entity-backend MR !1891). Each key maps to exactly ONE crm-svc permission,
 * the one `@RequirePermission` checks on the operation the control calls — the per-operation table is
 * crm-svc `src/common/access/crm-operation-inventory.ts`. So a control gated on its key is offered
 * exactly when the server would accept the call.
 *
 * Three rules shape the list:
 *  - `read` and `read-all` are separate keys. A role template that sees "all" holds `read-all`
 *    alone (ADR-044 §4, amended by LLD L3 §7 #24), and one CSV row maps one permission. A list or
 *    detail view checks EITHER — `<Can k={[...]}>` / `withPermission({ key: [...] })`.
 *  - `…:approvals:read` (open the queue) and `…:approve` (decide) both map to `crm_proposal:approve`;
 *    `settings:read` and `settings:configure` both map to `crm_settings:configure`. They are separate
 *    keys so a page and its controls read as what they do. Both rows of each pair are WRITE: acl-svc
 *    derives `kind` from the verb (`kindForRow`), so an acting-as support session holds neither.
 *  - Granted to the AGENCY persona only: no `crm_*` permission is granted to the platform-admin role.
 *  - `assign` and `export` are WRITE: one changes ownership, the other takes data out.
 *
 * Keys for modules crm-svc does not serve yet (leads, inbox, contacts, opportunities, catalog,
 * quotes, CDD, handover) are declared now so the catalogue is seeded once; no UI reads them until
 * the module's pages land. The inbox keys gate the conversation on a lead's detail page: there is no
 * separate Inbox tab (user decision 2026-10-31: new inquiries arrive as leads).
 *
 * ⚠️ UX only, FAIL-CLOSED. crm-svc authorizes every real call; an unseeded key hides its control.
 */
export const CrmAclKeys = {
  // ── Proposals (crm_proposal) ─────────────────────────────────────────────────────────────────
  /** Proposals the record scope lets the user see. Pair with `proposalsReadAll`. */
  proposalsRead: 'crm:proposals:read',
  /** Every proposal in the tenant. */
  proposalsReadAll: 'crm:proposals:read-all',
  /** Create, edit, lock, render, submit for approval, revise. */
  proposalsWrite: 'crm:proposals:write',
  /** Open the approval queue and the approver view (`crm_proposal:approve`). */
  proposalsApprovalsRead: 'crm:proposals:approvals:read',
  /** Approve or return a proposal (`crm_proposal:approve`). */
  proposalsApprove: 'crm:proposals:approve',
  /** Send for signature, retry a failed send. */
  proposalsProcess: 'crm:proposals:process',

  // ── Leads (crm_inquiry) ──────────────────────────────────────────────────────────────────────
  leadsRead: 'crm:leads:read',
  leadsReadAll: 'crm:leads:read-all',
  leadsWrite: 'crm:leads:write',
  /** Qualify, convert, disqualify. */
  leadsProcess: 'crm:leads:process',
  leadsAssign: 'crm:leads:assign',
  leadsExport: 'crm:leads:export',

  // ── Inbox (crm_conversation) ─────────────────────────────────────────────────────────────────
  inboxRead: 'crm:inbox:read',
  inboxReadAll: 'crm:inbox:read-all',
  inboxWrite: 'crm:inbox:write',
  inboxAssign: 'crm:inbox:assign',

  // ── Contacts and accounts (crm_account) ──────────────────────────────────────────────────────
  contactsRead: 'crm:contacts:read',
  contactsReadAll: 'crm:contacts:read-all',
  contactsWrite: 'crm:contacts:write',
  contactsAssign: 'crm:contacts:assign',
  contactsExport: 'crm:contacts:export',

  // ── Opportunities (crm_opportunity) ──────────────────────────────────────────────────────────
  opportunitiesRead: 'crm:opportunities:read',
  opportunitiesReadAll: 'crm:opportunities:read-all',
  opportunitiesWrite: 'crm:opportunities:write',
  /** Move stage, mark won or lost, reopen. */
  opportunitiesProcess: 'crm:opportunities:process',
  opportunitiesAssign: 'crm:opportunities:assign',
  opportunitiesExport: 'crm:opportunities:export',

  // ── Service catalog (crm_catalog) ────────────────────────────────────────────────────────────
  catalogRead: 'crm:catalog:read',
  catalogWrite: 'crm:catalog:write',
  catalogApprove: 'crm:catalog:approve',
  catalogConfigure: 'crm:catalog:configure',

  // ── Quotes (crm_quote) ───────────────────────────────────────────────────────────────────────
  quotesRead: 'crm:quotes:read',
  quotesReadAll: 'crm:quotes:read-all',
  quotesWrite: 'crm:quotes:write',
  quotesApprove: 'crm:quotes:approve',
  quotesProcess: 'crm:quotes:process',

  // ── Client due diligence (crm_cdd) ───────────────────────────────────────────────────────────
  cddRead: 'crm:cdd:read',
  cddReadAll: 'crm:cdd:read-all',
  cddWrite: 'crm:cdd:write',
  cddApprove: 'crm:cdd:approve',

  // ── Delivery handover (crm_handover) ─────────────────────────────────────────────────────────
  handoverRead: 'crm:handover:read',
  handoverReadAll: 'crm:handover:read-all',
  handoverWrite: 'crm:handover:write',
  /** Accept or return a handover line. */
  handoverProcess: 'crm:handover:process',

  // ── Settings (crm_settings) ──────────────────────────────────────────────────────────────────
  /** Open the CRM settings pages (`crm_settings:configure`). */
  settingsRead: 'crm:settings:read',
  /** Change a CRM setting (`crm_settings:configure`). */
  settingsConfigure: 'crm:settings:configure',

  // ── Field classes (ADR-045) ──────────────────────────────────────────────────────────────────
  /** A client's name, email and phone (`crm_field_kyc_pii:read`). */
  fieldKycPiiRead: 'crm:fields:kyc-pii:read',
  fieldCommercialRead: 'crm:fields:commercial:read',
  fieldInternalNoteRead: 'crm:fields:internal-note:read',
} as const;

export type CrmAclKey = (typeof CrmAclKeys)[keyof typeof CrmAclKeys];

/** Any-of sets: a view that both an "own" and an "all" holder may open. */
export const CrmAclAnyOf = {
  proposalsList: [CrmAclKeys.proposalsRead, CrmAclKeys.proposalsReadAll],
} as const;
